import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { PetInteractionLog, UserPet } from '@typings/pets/pet.interface';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import { useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { dateAgo } from '@utils/dateAgo';

interface Props {
  // 과거 호환성을 위해 남겨두지만 사용하지 않음 (원격 로그만 표시)
  logs?: PetInteractionLog[] | null;
  maxHeight?: number;
  showFilter?: boolean;
  autoScroll?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  pageSize?: number;
  activePet?: UserPet;
  onAppendLog?: (log: PetInteractionLog) => void; // 미사용
}

const InteractionLog: React.FC<Props> = ({
  logs,
  maxHeight = 200,
  showFilter = true,
  autoScroll = true,
  onLoadMore,
  hasMore = false,
  loading = false,
  pageSize = 50,
  activePet,
  onAppendLog,
}) => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(pageSize);
  const uid = useRecoilValue(currentUserIDState);

  // 원격 로그 구독: users/{uid}/pets/{petId}/logs
  const colPath =
    uid && activePet?.id
      ? `${CollectionPath.USERS}/${uid}/pets/${activePet.id}/logs`
      : null;
  const { data: remoteLogs } = useCollection<PetInteractionLog>(colPath, {
    listen: true,
    orderBy: ['createdAt', 'desc'],
  });

  // 안전한 로그 배열 생성 (원격 로그만 사용)
  const safeLogs = Array.isArray(remoteLogs) ? remoteLogs : [];

  // 타입 정규화: random_dialogue → user_interaction로 통일하여 표시
  // - 요청사항: [랜덤문구] 타입은 목록에서 제거하고, 모두 [상호작용]으로 표시
  // - 단, 원본 타입이 random_dialogue였던 항목만 [자동 생성] 태그 노출
  const normalizedLogs = useMemo(() => {
    return safeLogs.map((log: any) => ({
      ...log,
      _originalType: log.type,
      type: log.type === 'random_dialogue' ? 'user_interaction' : log.type,
    }));
  }, [safeLogs]);

  // 필터별 로그 분류
  const filteredLogs = normalizedLogs.filter((log: any) => {
    if (selectedFilter === 'all') return true;
    return log.type === selectedFilter;
  });

  // 표시할 로그 (최신 50개부터 시작)
  const displayedLogs = filteredLogs.slice(0, Math.max(0, visibleCount));

  // 자동 스크롤
  useEffect(() => {
    if (!autoScroll) return;
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  }, [logs, autoScroll, selectedFilter]);

  // 필터 변경 또는 pageSize 변경 시 초기화
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [selectedFilter, pageSize]);


  // 로그 타입별 아이콘
  const getLogIcon = (type: string) => {
    switch (type) {
      case 'user_interaction':
        return '🤝';
      case 'random_dialogue':
        return '🎲';
      case 'action':
        return '🎮';
      case 'growth':
        return '📈';
      default:
        return '📝';
    }
  };

  // 로그 타입별 색상
  const getLogColor = (type: string) => {
    switch (type) {
      case 'user_interaction':
        return '#4CAF50';
      case 'random_dialogue':
        return '#FF9800';
      case 'action':
        return '#2196F3';
      case 'growth':
        return '#9C27B0';
      default:
        return '#666';
    }
  };

  // 필터 옵션
  const filterOptions = [
    { value: 'all', label: '전체', icon: '📋' },
    { value: 'user_interaction', label: '상호작용', icon: '🤝' },
    { value: 'action', label: '액션', icon: '🎮' },
    { value: 'growth', label: '성장', icon: '📈' },
  ];

  // 더보기 클릭 시: 더 가져오기 + 더 보여주기
  const handleLoadMoreClick = () => {
    if (onLoadMore) onLoadMore();
    setVisibleCount((prev) => prev + pageSize);
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  };

  return (
    <Container>
      {showFilter && (
        <FilterSection>
          {filterOptions.map((option) => (
            <FilterButton
              key={option.value}
              onClick={() => setSelectedFilter(option.value)}
              $isActive={selectedFilter === option.value}
            >
              <span style={{ fontSize: 12 }}>{option.icon}</span>
              <span style={{ fontWeight: 500 }}>{option.label}</span>
            </FilterButton>
          ))}
        </FilterSection>
      )}

      <LogContainer ref={logContainerRef} style={{ maxHeight }}>
        {loading && hasMore && (
          <LoadingIndicator>
            <LoadingSpinner />
            이전 로그를 불러오는 중...
          </LoadingIndicator>
        )}

        {displayedLogs.length === 0 ? (
          <EmptyMessage>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyText>
              {selectedFilter === 'all'
                ? '아직 상호작용 기록이 없어요'
                : '해당하는 로그가 없어요'}
            </EmptyText>
          </EmptyMessage>
        ) : (
          displayedLogs.map((log, index) => (
            <LogItem key={`${log.id}-${index}`}>
              <LogIcon $color={getLogColor(log.type)}>
                {getLogIcon(log.type)}
              </LogIcon>
              <LogContent>
                <LogMessage>{log.message}</LogMessage>
                <LogMeta>
                  <LogTime>{dateAgo(log.createdAt)}</LogTime>
                  {(log.isAutoGenerated ||
                    log._originalType === 'random_dialogue') && (
                      <AutoGenerated>랜덤 문구</AutoGenerated>
                    )}
                </LogMeta>
              </LogContent>
            </LogItem>
          ))
        )}
      </LogContainer>

      {!loading && (hasMore || visibleCount < filteredLogs.length) && (
        <LoadMoreButton onClick={handleLoadMoreClick}>더보기</LoadMoreButton>
      )}
    </Container>
  );
};

export default InteractionLog;

// 🎨 스타일 컴포넌트
const Container = styled.div`
  border-radius: 12px;
  border: 1px solid var(--lineColor);
  overflow: hidden;
  margin-top: 24px;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  overflow-x: auto;
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? 'transparent' : 'var(--lineColor)')};
  background: ${({ $isActive }) =>
    $isActive ? 'var(--buttonBackgroundColor)' : 'transparent'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--buttonTextColor)' : 'var(--textColor)'};
  border-radius: 16px;
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: var(--inkColor);
  }
`;

const LogContainer = styled.div`
  overflow-y: auto;
  ${scrollbarStyle}
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: var(--subTextColor);
  font-size: 12px;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top-color: #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--subTextColor);
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const EmptyText = styled.div`
  font-size: 13px;
  text-align: center;
  line-height: 1.4;
`;

const LogItem = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 8px;
  color: var(--textColor);
  border-bottom: 1px solid var(--cardLineColor);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--inkColor);
  }
`;

const LogIcon = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background: ${({ $color }) => $color}22;
  color: ${({ $color }) => $color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const LogContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const LogMessage = styled.div`
  font-size: 13px;
  color: var(--textColor);
  line-height: 1.4;
  margin-bottom: 4px;
  word-break: break-word;
`;

const LogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogTime = styled.div`
  font-size: 9px;
  color: var(--subTextColor);
`;

const AutoGenerated = styled.div`
  font-size: 10px;
  color: var(--subColor);
  background: var(--primaryColor);
  padding: 1px 4px;
  border-radius: 3px;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--buttonBackgroundColor);
  border: none;
  color: var(--buttonTextColor);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--inkColor);
  }
`;
